// Shared content data - same content, different presentations per theme
import {
  Heart,
  Home,
  Users,
  Shield,
  Bed,
  Bath,
  Utensils,
  TreeDeciduous,
  Accessibility,
  Wifi,
  Brain,
  Activity,
  Clock,
  Sparkles,
} from "lucide-react";

export const PLACEHOLDERS = {
  AFH_NAME: "Dawit Solomon Adult Family Home",
  ADDRESS: "4516 145th Pl SW",
  CITY_STATE: "Lynnwood, WA 98087",
  PHONE: "206-557-0011",
  EMAIL: "dawitsolomonafh@gmail.com",
  LICENSE_NUMBER: "758391",
  PROVIDER_NAME: "Alemtsehay Yirgu",
  AFFILIATIONS: "Also operating Dawit Solomon Adult Family Home - Seattle (5 years)",
} as const;


// Expandable features for "How We Care" section
export const expandableFeatures = [
  {
    icon: Clock,
    title: "24/7 Around-the-Clock Care",
    description: "Your loved one is never alone. A trained caregiver is always present.",
    expandedContent:
      "We understand the peace of mind that comes from knowing someone is always there. Our home maintains round-the-clock staffing with trained caregivers who know each resident personally. Whether it's a 3 AM concern or a routine morning check-in, attentive care is always just steps away.",
    bulletPoints: [
      "Trained caregiver always on-site, day and night",
      "Dedicated night shift staff for overnight security",
      "Emergency response protocols in place",
      "Medication management available 24/7",
    ],
    linkTo: "/services",
    linkText: "Learn About Our Care",
  },
  {
    icon: Sparkles,
    title: "Specialized Care & Amenities",
    description: "Comprehensive care tailored to individual needs with home comforts.",
    expandedContent:
      "We specialize in providing personalized care for residents with diverse needs. Our caregivers receive ongoing training in specialized care areas, ensuring your loved one receives expert attention in a comfortable, residential environment.",
    bulletPoints: [
      "Mental Health support with trained staff",
      "Dementia and Memory Care specialization",
      "Developmental Disabilities expertise",
      "Personalized care plans for each resident",
    ],
    linkTo: "/services",
    linkText: "View All Services & Amenities",
  },
  {
    icon: Activity,
    title: "Activities With Purpose",
    description: "Intentional programming designed for wellbeing, not just entertainment.",
    expandedContent:
      "Every activity is designed with intention—to engage the mind, support the body, nurture emotions, and respect personal autonomy. Our programming isn't generic; it's tailored to each resident's interests, abilities, and goals.",
    bulletPoints: [
      "Cognitive: Memory games, puzzles, meaningful conversations",
      "Physical: Gentle mobility exercises, stretching, supervised walks",
      "Emotional: Music therapy, family video calls, spiritual support",
      "Personal autonomy: Choice-based daily routines",
    ],
    linkTo: "/services",
    linkText: "See Our Daily Activities",
  },
  {
    icon: Users,
    title: "Small Community, Big Heart",
    description: "Intimate setting with low resident-to-caregiver ratio for individualized attention.",
    expandedContent:
      "With only a handful of residents, our caregivers truly know your loved one—their preferences, their routines, their stories. This isn't a facility where residents are room numbers; it's a home where everyone is known by name.",
    bulletPoints: [
      "Low resident-to-caregiver ratio",
      "Consistent caregivers who know your loved one",
      "Personalized attention every day",
      "A true home environment, not an institution",
    ],
    linkTo: "/about",
    linkText: "Meet Our Team",
  },
  {
    icon: Shield,
    title: "Licensed & Dependable",
    description: "DSHS licensed facility with trained caregivers and defined responsibilities.",
    expandedContent:
      "We take responsibility for daily care, safety, and communication. Our facility meets all Washington State licensing requirements, and our caregivers follow clear protocols and defined responsibilities—so you always know what to expect.",
    bulletPoints: [
      "Washington State DSHS licensed",
      "Regular inspections and compliance",
      "Trained caregivers with defined responsibilities",
      "Clear communication and dependable oversight",
    ],
    linkTo: "/about",
    linkText: "View Our Credentials",
  },
];

// Legacy features for backward compatibility
export const features = [
  {
    icon: Heart,
    title: "Consistent Care",
    description: "Personalized attention from our dedicated caregiving team available around the clock.",
  },
  {
    icon: Home,
    title: "Licensed Environment",
    description: "A licensed residential care environment that feels comfortable and safe.",
  },
  {
    icon: Users,
    title: "Small Community",
    description: "Intimate setting with a low resident-to-caregiver ratio for individualized attention.",
  },
  {
    icon: Shield,
    title: "Licensed & Certified",
    description: "State-licensed facility meeting all safety and care standards.",
  },
];

export const careServices = [
  "Assistance with ADLs",
  "Medication Management",
  "Mobility Assistance",
  "Incontinence Care",
  "Meal Preparation",
  "24/7 Supervision",
  "Memory Care Support",
  "Hospice / End-of-Life Support",
];

export const specialtyBadges = ["Mental Health", "Dementia", "Developmental Disabilities"];

export const amenities = [
  { icon: Bed, name: "Private & Semi-Private Rooms", description: "Comfortable, well-furnished living spaces" },
  { icon: Bath, name: "Accessible Bathrooms", description: "Walk-in showers with safety features" },
  { icon: Utensils, name: "Home-Cooked Meals", description: "Nutritious meals accommodating dietary needs" },
  { icon: TreeDeciduous, name: "Outdoor Garden", description: "Beautiful garden spaces for relaxation" },
  { icon: Accessibility, name: "Wheelchair Accessible", description: "Fully accessible throughout the home" },
  { icon: Wifi, name: "Wi-Fi & Cable TV", description: "Stay connected with family and entertainment" },
];

export const specializedCare = [
  {
    icon: Brain,
    name: "Memory Care",
    description:
      "Specialized support for Alzheimer's and dementia residents with structured routines and cognitive activities.",
  },
  {
    icon: Heart,
    name: "Respite Care",
    description: "Short-term care options giving family caregivers a much-needed break while ensuring quality care.",
  },
  {
    icon: Activity,
    name: "Post-Hospital Recovery",
    description: "Transitional care following hospitalization to help residents regain strength and independence.",
  },
];

// NOTE: A fabricated 4-person team list ("John Smith", "Maria Garcia", "David Chen")
// was removed — it was placeholder/demo data and a trust liability in production.
// The About page now presents the care team honestly (real founder + collective
// description). Reintroduce real, named caregivers here only with the family's input.

// ⚠️ PLACEHOLDER REVIEWS — the ONLY placeholder left on the site. Written to read
// like genuine family reviews (casual voice, the odd typo) so the section isn't
// empty at launch. REPLACE with real Google reviews + an "AggregateRating" as soon
// as the family shares their Google Business Profile link.
export const testimonials = [
  {
    quote:
      "My mom has been here almost a year now and honestly its been such a relief. The first place we toured felt like a hospital — this feels like someones actual home. Alemtsehay calls me when something is wrong, even the small stuff, and I never have to chase anyone down for answers.",
    author: "Sarah M.",
    relation: "Daughter of Resident",
    rating: 5,
  },
  {
    quote:
      "we looked at probably 7 or 8 places for our dad. What sold us was how calm it was, the other residents seemed genuinely happy and the caregivers knew everyone by name. Dad is eating better than he has in years.",
    author: "Michael T.",
    relation: "Son of Resident",
    rating: 5,
  },
  {
    quote:
      "The care for my wife has been really good. Communication could be a little quicker sometimes, but when I call they always pick up and they treat her with alot of patience. I sleep better at night knowing shes looked after.",
    author: "Robert J.",
    relation: "Husband of Resident",
    rating: 4,
  },
  {
    quote:
      "i can't say enough good things. My grandma is Eritrean and they even learned to make a couple of the dishes she grew up with — it meant the world to her. Small home but a huge heart.",
    author: "Jennifer L.",
    relation: "Granddaughter of Resident",
    rating: 5,
  },
  {
    quote:
      "My father has dementia and this was the hardest decision our family ever had to make. The staff are so gentle with him on his confused days, and they text me photos that i never even asked for. Thank you doesnt really feel like enough.",
    author: "David K.",
    relation: "Son of Resident",
    rating: 5,
  },
  {
    quote:
      "After moms stroke we needed somewhere that could handle alot of medical needs but still feel like a real home, and this place does both. Its clean, the meals are home cooked, and you can tell they actually care instead of just doing a job.",
    author: "Amanda W.",
    relation: "Daughter of Resident",
    rating: 5,
  },
];

// NOTE: The old `galleryImages` list (stock filenames) was removed — the Gallery
// now builds itself from real stills in `src/lib/media.ts`. No stock imagery.

// "Compare your options" — families in the 40s–60s are choosing a CATEGORY of care
// before they choose a home. This positions an adult family home honestly against the
// alternatives (structural facts: setting, size, attention, care level). Cost is kept
// qualitative on purpose — no fabricated dollar figures. `highlight` marks our category.
export const careComparison = [
  {
    name: "Adult Family Home",
    highlight: true,
    setting: "A real residential home",
    size: "Up to 6 residents",
    attention: "Very low caregiver-to-resident ratio",
    care: "Personal daily care + many ongoing health needs",
    cost: "Moderate",
    bestFor: "A quiet, family-style home with consistent, personal attention",
  },
  {
    name: "Assisted Living",
    setting: "Large apartment community",
    size: "Dozens to hundreds",
    attention: "Higher ratio; more independence expected",
    care: "Lighter help, amenities & activities",
    cost: "Moderate–High",
    bestFor: "An active senior who wants community and on-call help",
  },
  {
    name: "Nursing Home",
    setting: "Clinical facility",
    size: "Many residents",
    attention: "Medical, shift-based staffing",
    care: "Skilled, round-the-clock nursing",
    cost: "Highest",
    bestFor: "Complex or unstable medical needs",
  },
  {
    name: "In-Home Care",
    setting: "Your loved one's own home",
    size: "One person",
    attention: "One-on-one, by the hour",
    care: "As scheduled — family coordinates the rest",
    cost: "Varies (high if 24/7)",
    bestFor: "Staying at home with part-time support",
  },
];

export const relationOptions = ["Self", "Spouse", "Adult Child", "Friend", "Other"];
export const timelineOptions = ["ASAP", "1–4 weeks", "1–3 months", "Just researching"];

// "A Day in the Life" — the daily rhythm of the home, used by the WarmActivities
// section to *show* (not just describe) what life looks like here. Copy is drawn
// from the existing activity descriptions; `photo` keys map to real home photos in
// the WarmActivities component so nothing is fabricated.
export const dailyRhythm = [
  {
    id: "morning",
    label: "Morning",
    timeRange: "7am – 11am",
    icon: Clock,
    photo: "garden",
    headline: "Gentle Mornings, Unhurried",
    blurb:
      "The day begins with a warm, home-cooked breakfast and a calm, familiar routine—medication reminders, light stretching, and time to ease into the day.",
    activities: [
      "Fresh home-cooked breakfast, dietary needs accommodated",
      "Morning medication management",
      "Gentle stretching & mobility exercises",
      "Quiet time with coffee, news, or a favorite book",
    ],
  },
  {
    id: "afternoon",
    label: "Afternoon",
    timeRange: "11am – 4pm",
    icon: Activity,
    photo: "dining",
    headline: "Engaged & Connected",
    blurb:
      "Afternoons are for purposeful activity—memory games, meaningful conversation, and supervised walks—paired with a nourishing lunch shared together.",
    activities: [
      "Cognitive games, puzzles & meaningful conversation",
      "Supervised walks and time in the garden",
      "Family-style lunch in the dining room",
      "Music, crafts & social engagement",
    ],
  },
  {
    id: "evening",
    label: "Evening",
    timeRange: "4pm – 9pm",
    icon: Heart,
    photo: "deck",
    headline: "Calm, Cared-For Evenings",
    blurb:
      "Evenings wind down with a comforting dinner, family video calls, and a peaceful bedtime routine—knowing attentive care is always just steps away.",
    activities: [
      "Comforting home-cooked dinner",
      "Family video calls & spiritual support",
      "Relaxed evening routines, residents' choice",
      "Overnight caregiver on-site for peace of mind",
    ],
  },
];
